import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { MediaRecorderUploadDialogComponent } from '../media-recorder-upload-dialog/media-recorder-upload-dialog.component';
import { MediaUploadService } from '../media-upload.service';

@Component({
  selector: 'sha-media-upload-input',
  templateUrl: './media-upload-input.component.html',
  styleUrls: ['./media-upload-input.component.css'],
})
export class MediaUploadInputComponent implements OnInit {
  useDefaultCameraApp$: Observable<boolean>;

  @Input() params: any;
  @Input() disabled?: boolean;
  @Input() displayText?: boolean;

  constructor(
    private dialog: MatDialog,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService,
    private mediaUploadService: MediaUploadService
  ) {}

  ngOnInit(): void {
    this.useDefaultCameraApp$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((employee) => employee !== null),
      take(1),
      switchMap((employee) => {
        return this.systemControlStateService
          .getSystemControlValues(['CL_POWER_LANE_MEDIA_RECORDER'], employee.rooftopId)
          .pipe(
            take(1),
            map((scts) => {
              return scts['CL_POWER_LANE_MEDIA_RECORDER'].value === '1' ? true : false;
            })
          );
      })
    );
  }

  onFileSelected(e: any) {
    const files = e.target.files || e.dataTransfer.files;
    if (!files.length) return;

    const file = files[0];

    if (!(file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      alert('Invalid file type. Please select an image or video file.');
      e.target.value = '';
      return;
    }

    this.createImage(file);
  }

  createImage(file: File) {
    if (!file || file.size === 0) {
      alert('Invalid file provided.');
      return;
    }

    const fileName: string = this.mediaUploadService.generateId(12) + file.name.substring(file.name.lastIndexOf('.'));

    if (file.type.startsWith('video/')) {
      this.processVideo(file, fileName);
    } else if (file.type.startsWith('image/')) {
      this.uploadMedia(file, fileName, null);
    } else {
      alert('Invalid file type. Please select an image or video file.');
    }
  }

  processVideo(file: File, fileName: string) {
    if (!file || file.size === 0) {
      alert('Invalid file provided.');
      return;
    }

    // Probe duration from the raw File — reading the whole file into memory as a base64
    // data URL blows the mobile browser memory ceiling for large native-camera videos.
    const url = (URL || webkitURL).createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    const cleanup = () => {
      (URL || webkitURL).revokeObjectURL(url);
      video.remove();
    };

    video.addEventListener('loadedmetadata', () => {
      const duration: number = video.duration;
      cleanup();
      this.uploadMedia(file, fileName, duration);
    });

    // If metadata can't be read, still upload rather than silently dropping the file.
    video.addEventListener('error', () => {
      cleanup();
      this.uploadMedia(file, fileName, null);
    });

    video.src = url;
  }

  private uploadMedia(file: File, fileName: string, duration: number | null) {
    this.mediaUploadService.uploadFile(
      this.params.caption,
      this.params.customerFacing,
      fileName,
      file.type,
      duration,
      file.size,
      file,
      this.params
    );
  }

  mediaRecorderUpload() {
    this.dialog.open(MediaRecorderUploadDialogComponent, {
      width: '80%',
      maxWidth: '600px',
      maxHeight: '800px',
      panelClass: 'media-recorder-dialog',
      disableClose: true,
      data: {
        caption: this.params.caption,
        customerFacing: this.params.customerFacing,
        params: this.params,
      },
    });
  }
}
